// Global Instructions Rule Applied!

import React, { useState, useEffect, useCallback, forwardRef } from 'react'
import Select from '../../Select.jsx'
import { useCountryLocalSearch } from '../../../hooks_deprecated/useCountryLocalSearch.js'
import { InputSize, FieldValue } from '../types.ts'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

export interface CountrySelectProps {
  value: FieldValue
  onChange?: (value: string, option?: SelectListEntry | null) => void
  onSelect?: (value: string, option?: SelectListEntry) => void
  onClear?: () => void
  mode?: 'form' | 'inline' | 'cell'
  contextKey?: string
  required?: boolean
  placeholder?: string
  size?: InputSize
  error?: boolean
  style?: React.CSSProperties
  className?: string
  autoFocus?: boolean
  onKeyDown?: (e: React.KeyboardEvent) => void
  disabled?: boolean
}

export const CountrySelect = forwardRef<any, CountrySelectProps>(
  (
    {
      value,
      onChange,
      onSelect,
      onClear,
      mode = 'form',
      contextKey,
      required = false,
      placeholder = 'Search countries...',
      size = 'small',
      error = false,
      style = {},
      className = '',
      autoFocus,
      onKeyDown,
      disabled,
      ...selectProps
    },
    ref
  ) => {
    const {
      searchCountries,
      lookupCountryByCode,
      loading: countrySearchLoading,
      results: countrySearchResults,
      clearResults: clearCountryResults,
      hasSearched: countryHasSearched
    } = useCountryLocalSearch()

    const [selectedCountry, setSelectedCountry] = useState<SelectListEntry | null>(null)
    const [hasBeenCleared, setHasBeenCleared] = useState(false)

    const initializeCountrySelection = useCallback(
      async (countryVal: any) => {
        // Handle object values: extract value and label if countryVal is an object
        let countryCode: string | null = null
        let countryLabel: string | null = null

        if (countryVal && typeof countryVal === 'object' && countryVal !== null) {
          // Extract value (country code) and label (country name) from object
          countryCode = countryVal.value || null
          countryLabel = countryVal.label || null
        } else if (countryVal) {
          // Handle string values (country code or label)
          countryCode = typeof countryVal === 'string' ? countryVal : null
          countryLabel = typeof countryVal === 'string' ? countryVal : null
        }

        // If no country value, clear selection
        if (!countryCode && !countryLabel) {
          setSelectedCountry(null)
          return
        }

        // Check if we already have this country selected
        const currentValue = countryCode || countryLabel
        if (selectedCountry && selectedCountry.value === currentValue) {
          return
        }

        // Check if country exists in search results
        const existingCountry = countrySearchResults.find(
          (country: any) =>
            country.value === countryCode || country.value === countryLabel || country.label === countryLabel
        )

        if (existingCountry) {
          const selectListEntry: SelectListEntry = {
            label: existingCountry.label,
            value: existingCountry.value,
            name: existingCountry.name || existingCountry.label
          }
          setSelectedCountry(selectListEntry)
        } else {
          // Try to determine if we have a country code
          const codeToCheck = countryCode || countryLabel
          const isCountryCode = codeToCheck && /^[A-Z]{2}$/.test(codeToCheck)

          if (isCountryCode) {
            try {
              const countryData = await lookupCountryByCode(codeToCheck)
              if (countryData) {
                const selectListEntry: SelectListEntry = {
                  label: countryData.label,
                  value: countryData.value,
                  name: countryData.name || countryData.label
                }
                setSelectedCountry(selectListEntry)
              } else {
                // Fallback: use provided label or code
                const tempCountry: SelectListEntry = {
                  label: countryLabel || codeToCheck,
                  value: codeToCheck,
                  name: countryLabel || codeToCheck
                }
                setSelectedCountry(tempCountry)
              }
            } catch (error) {
              console.warn('Error looking up country:', error)
              // Fallback: use provided label or code
              const tempCountry: SelectListEntry = {
                label: countryLabel || codeToCheck,
                value: codeToCheck,
                name: countryLabel || codeToCheck
              }
              setSelectedCountry(tempCountry)
            }
          } else {
            // Not a country code, treat as label/name
            const tempCountry: SelectListEntry = {
              label: countryLabel || codeToCheck || '',
              value: codeToCheck || countryLabel || '',
              name: countryLabel || codeToCheck || ''
            }
            setSelectedCountry(tempCountry)
          }
        }
      },
      [countrySearchResults, lookupCountryByCode, selectedCountry]
    )

    useEffect(() => {
      if (hasBeenCleared && value) {
        setHasBeenCleared(false)
      }

      initializeCountrySelection(value)
    }, [value, initializeCountrySelection, hasBeenCleared])

    useEffect(() => {
      if (!value && selectedCountry) {
        setSelectedCountry(null)
      }
    }, [value, selectedCountry])

    const handleCountrySelect = useCallback(
      (selectedValue: any, option: any) => {
        if (option) {
          setSelectedCountry(option)
          onChange?.(selectedValue, option)
          onSelect?.(selectedValue, option)
        }
      },
      [onChange, onSelect]
    )

    const handleClear = useCallback(() => {
      setSelectedCountry(null)
      setHasBeenCleared(true)
      clearCountryResults()
      onChange?.('', null)
      onClear?.()
    }, [onChange, onClear, clearCountryResults])

    const handleSearch = useCallback(
      (searchValue: string) => {
        if (searchValue && searchValue.length >= 2) {
          searchCountries(searchValue)
        } else if (!searchValue) {
          clearCountryResults()
        }
      },
      [searchCountries, clearCountryResults]
    )

    const transformedResults: SelectListEntry[] = countrySearchResults.map((result: any) => ({
      label: result.label,
      value: result.value,
      name: result.name || result.label
    }))

    const options: SelectListEntry[] = selectedCountry
      ? [selectedCountry, ...transformedResults.filter((result) => result.value !== selectedCountry.value)]
      : countryHasSearched
        ? transformedResults
        : []

    const placeholderText = required ? `${placeholder}` : placeholder

    return (
      <Select
        ref={ref}
        value={selectedCountry ? selectedCountry.value : undefined}
        onChange={handleCountrySelect}
        onClear={handleClear}
        onSearch={handleSearch}
        loading={countrySearchLoading}
        options={options}
        placeholder={placeholderText}
        size={size}
        style={{
          width: '100%',
          ...style,
          borderColor: style.borderColor || (error ? '#ff4d4f' : undefined)
        }}
        className={className}
        status={error ? 'error' : undefined}
        filterOption={false}
        notFoundContent={
          countrySearchLoading ? 'Searching...' : countryHasSearched ? 'No countries found' : 'Type to start searching'
        }
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
        {...selectProps}
      />
    )
  }
)

CountrySelect.displayName = 'CountrySelect'
